import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Modal, { ModalFooter } from '../ui/Modal';
import Button from '../ui/Button';
import { useI18n } from '../../context/I18nContext';
import { useConsent } from '../../context/ConsentContext';
import { clickableLinks } from '../../styles/colors';
import { PRIVACY_COOKIES_PATH } from '../../utils/legalLinks';

const CookieToggle: React.FC<{
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}> = ({ checked, disabled = false, onChange, label }) => {
  const { t } = useI18n();

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`
        relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
        ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
        ${checked ? 'bg-blue-600' : 'bg-gray-300'}
      `}
    >
      <span className="sr-only">{label}</span>
      <span
        aria-hidden
        className={`
          inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform
          ${checked ? 'translate-x-5' : 'translate-x-0.5'}
        `}
      />
      <span className="sr-only">{checked ? t('common.on') : t('common.off')}</span>
    </button>
  );
};

const CookiePreferencesModal: React.FC = () => {
  const { t } = useI18n();
  const { marketing, savePreferences, isPreferencesOpen, closePreferences } = useConsent();
  const [draftMarketing, setDraftMarketing] = useState(marketing);

  useEffect(() => {
    if (isPreferencesOpen) {
      setDraftMarketing(marketing);
    }
  }, [isPreferencesOpen, marketing]);

  const handleSave = () => {
    savePreferences({ marketing: draftMarketing });
  };

  return (
    <Modal
      isOpen={isPreferencesOpen}
      onClose={closePreferences}
      title={t('cookies.preferencesTitle')}
      size="md"
      footer={
        <ModalFooter>
          <Button type="button" variant="ghost" size="md" onClick={closePreferences}>
            {t('common.cancel')}
          </Button>
          <Button type="button" variant="primary" size="md" onClick={handleSave}>
            {t('cookies.savePreferences')}
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-5">
        <p className="text-sm text-gray-600">{t('cookies.preferencesDescription')}</p>

        <div className="space-y-3">
          <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900">
                  {t('cookies.categoryNecessary')}
                </p>
                <p className="mt-1 text-sm text-gray-600">
                  {t('cookies.categoryNecessaryDescription')}
                </p>
              </div>
              <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                {t('cookies.alwaysActive')}
              </span>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900">
                  {t('cookies.categoryMarketing')}
                </p>
                <p className="mt-1 text-sm text-gray-600">
                  {t('cookies.categoryMarketingDescription')}
                </p>
              </div>
              <CookieToggle
                checked={draftMarketing}
                onChange={setDraftMarketing}
                label={t('cookies.categoryMarketing')}
              />
            </div>
          </div>
        </div>

        <p className="text-xs text-gray-500">
          {t('cookies.learnMore')}{' '}
          <Link
            to={PRIVACY_COOKIES_PATH}
            className={`${clickableLinks} underline`}
            onClick={closePreferences}
          >
            {t('cookies.privacyLink')}
          </Link>
        </p>
      </div>
    </Modal>
  );
};

export default CookiePreferencesModal;
